/**
 * Buying a term outright — the flow that replaced the mandate.
 *
 * A practice picks a length, pays for it once, and it runs for the months it
 * covers. Nothing renews itself, nothing is cancelled, and nothing is refunded:
 * a term simply ends, and the next one is another purchase. That is why these
 * shapes carry a *term* and an *order* rather than a subscription and a cycle —
 * there is no mandate to be in a state about, only days that have been paid for
 * and a payment that either settled or did not.
 *
 * Two halves, the way every feature here splits them: the shapes the screens
 * read, and — below the rule — the snake_case wire shapes only
 * `prepaidService` should ever touch.
 *
 * Money is rupees as a number on this side and a decimal string on the wire,
 * as everywhere else in this feature. The one figure that is neither is the
 * gateway's `amount`, which is **integer paise**: see `PrepaidCheckout`, where
 * it is kept exactly as it arrived.
 *
 * The recurring shapes in `subscriptions.types` are untouched and still typed.
 * A workspace that signed a mandate before this is still the backend's to
 * answer for; none of the screens read it any more.
 */

import type { BillingCycleId } from "./billing.types";

/**
 * What the server made of the purchase being priced — and it is the server's
 * to decide, never sent and never inferred from a figure on the response.
 *
 * - `NEW` — nothing is running. The term starts the day it is paid for.
 * - `RENEWAL` — the same plan again, queued behind the term running now and
 *   starting the day after it ends.
 * - `UPGRADE` — a longer term, starting immediately, crediting whatever is
 *   left of the one it replaces.
 * - `DOWNGRADE` — a shorter term, queued like a renewal. No credit, and no
 *   money back for the difference.
 * - `INTERNAL_CONVERSION` — the term running now is an Internal Billing
 *   one-off, and this buys out of it. Credited like an upgrade.
 *
 * The two credited kinds need a backend flag that is off by default, so they
 * quote happily and are refused at the order. See `isCreditKind`.
 */
export type PurchaseKind =
  | "NEW"
  | "RENEWAL"
  | "UPGRADE"
  | "DOWNGRADE"
  | "INTERNAL_CONVERSION";

/**
 * Where a term stands.
 *
 * - `ACTIVE` — the one running. Grants access through its last day.
 * - `SCHEDULED` — paid for and waiting its turn. **It grants access from its
 *   own start date**, whether or not the backend's sweep has got round to
 *   promoting it, which is why a `current` term can read `SCHEDULED` for the
 *   first few minutes of its first day.
 * - `EXPIRED` — over, either by running out or by an upgrade replacing it.
 */
export type PrepaidTermStatus = "ACTIVE" | "SCHEDULED" | "EXPIRED";

/**
 * Where the term came from.
 *
 * - `PREPAID` — bought through this flow.
 * - `ONE_TIME` — granted by Internal Billing, which is what makes a purchase
 *   an `INTERNAL_CONVERSION` rather than a renewal of it.
 */
export type TermBillingType = "PREPAID" | "ONE_TIME";

/**
 * Where a payment got to.
 *
 * - `CREATED` — open and payable. The only status that carries a `checkout`.
 * - `PAID` — settled, with a `term` on it. Final.
 * - `SUPERSEDED` — the practice started a different purchase, or another order
 *   settled first.
 * - `EXPIRED` — the half hour ran out with nothing paid.
 * - `FAILED` — either the gateway refused to open an order, or money was taken
 *   and bought nothing. Final, and the second case is somebody's to sort out
 *   by hand.
 *
 * `SUPERSEDED` and `EXPIRED` are not quite dead: a payment captured late can
 * still settle one, if the term can still be placed. `PAID` and `FAILED` are
 * the only two that are finished with.
 */
export type PrepaidOrderStatus =
  | "CREATED"
  | "PAID"
  | "SUPERSEDED"
  | "EXPIRED"
  | "FAILED";

/** A term, as the screens read one: a plan and the days it covers. */
export interface PrepaidTerm {
  /** `"PRO_3M"`. The backend's own name for the plan that was bought. */
  planCode: string;
  billingType: TermBillingType;
  status: PrepaidTermStatus;
  /** ISO days — `"2026-10-10"` — on the IST calendar, which is the clock
   *  access actually ends on. The last day is granted whole. */
  startsOn: string;
  endsOn: string;
}

/**
 * The term running now and the one bought to follow it.
 *
 * **This is "your plan" and "your next plan"**, and the only place the second
 * one can be read at all. `GET /billing/subscription/` shows the active row and
 * nothing else, so a renewal already paid for is invisible there.
 *
 * Both `null` is an ordinary answer: a practice that has never bought a term,
 * and also one still on an old mandate — the backend goes quiet on that case
 * here and names it when a quote is asked for.
 */
export interface PrepaidTerms {
  current: PrepaidTerm | null;
  queued: PrepaidTerm | null;
}

/**
 * What is being credited back for a term cut short, and the arithmetic behind
 * it — which the summary shows, because a figure subtracted from a price with
 * no explanation is a figure somebody will query.
 *
 * Whole IST days, counting today as unused. It is never cash: it comes off this
 * purchase or it is not had at all.
 */
export interface TermCredit {
  /** What comes off, in rupees. */
  amount: number;
  /** What it was worked out from: the old term's price after its own coupon,
   *  before tax. */
  basisAmount: number;
  unusedDays: number;
  termDays: number;
}

/**
 * What a purchase costs, line by line, exactly as the server worked it out.
 *
 * Deliberately not `PriceBreakdown` from `billing.types`. That one is the
 * recurring checkout's, it has a `renewsOn` on it, and there is nothing for a
 * credit line to go — a term being bought out mid-way is a case the old
 * arithmetic never had. Widening the shared shape would put a field on the
 * recurring summary that can never be anything but zero.
 *
 * The order the lines are read in is the order they are applied:
 * `base − discount − credit = taxable`, then tax on top of that.
 */
export interface PrepaidPrice {
  /** How many months the charge covers: 1, 3, 6 or 12. */
  months: number;
  /** The plan's own price before anything is taken off, pre-tax. */
  base: number;
  /** What the coupon took off, or 0. */
  discount: number;
  /** What the unused days of a replaced term took off, or 0. Always equal to
   *  `credit.amount` where there is a credit at all. */
  credit: number;
  /** What the tax is charged on: base less discount less credit. */
  taxable: number;
  /** The three-way split. Only `igst` is ever non-zero today, and the other two
   *  are carried because the invoice carries them and because the day a
   *  registered practice is billed in-state they will not be. */
  cgst: number;
  sgst: number;
  igst: number;
  /** All three together — what the summary's "GST" line shows. */
  tax: number;
  /** The rate as the server printed it, for the label beside that line:
   *  `"18"` from a quote, `"18.00"` from an order. A string because the two
   *  spellings are the same rate and comparing them as numbers invites a
   *  mismatch that means nothing. */
  taxRateLabel: string;
  /** The invoice total. */
  total: number;
  /**
   * What is actually charged, and the only figure the Buy button may show.
   *
   * Always equal to `total` as the backend stands today, and carried separately
   * anyway: it is the field the order is checked against, so a build that reads
   * the total where it should read this would pass a staleness check it had not
   * actually made.
   */
  amountDue: number;
}

/**
 * A price for a purchase that has not been made: what it would cost, what it
 * would buy, and what the server thinks it is.
 *
 * Records nothing — no row, no held coupon, no gateway call — so it can be
 * asked as often as the reader changes their mind. It goes stale on one axis
 * only, and sharply: a credit is counted in whole IST days, so it drops at
 * midnight there. Quote again before the Buy button is pressed.
 */
export interface PrepaidQuote {
  purchaseKind: PurchaseKind;
  planCode: string;
  /** The term this would buy **if it were paid for today**. Settlement dates
   *  the real one from the day the money was captured. */
  startsOn: string;
  endsOn: string;
  /** The term this follows or replaces, or `null` for a first purchase. */
  currentTerm: PrepaidTerm | null;
  /** Only ever set on `UPGRADE` and `INTERNAL_CONVERSION`. */
  credit: TermCredit | null;
  /** The coupon the server accepted, or `null` where none was asked for or
   *  none held. A code that was sent and refused is an error, not a quote. */
  coupon: string | null;
  price: PrepaidPrice;
}

/**
 * What Razorpay's window needs, as the order handed it over.
 *
 * Passed through to the gateway as it arrived, with only a handler of our own
 * added. The key is the **public** one; a `key_secret` appearing on a response
 * this app reads would be an incident rather than a field to map.
 *
 * Note this is an *order* handoff, not the mandate one `CheckoutHandoff` in
 * `subscriptions.types` describes. Different id, different gateway mode, and an
 * amount that has to travel — the window cannot price an order itself.
 */
export interface PrepaidCheckout {
  /** `rzp_test_…` / `rzp_live_…`. */
  keyId: string;
  /** Razorpay's own order id, `order_…` — not our `public_id`. */
  orderId: string;
  /**
   * What to charge, in **integer paise**, exactly as the backend sent it.
   *
   * Not converted to rupees and not re-derived from `amountDue`: this is the
   * figure the gateway signs against, and a rounding of our own between the
   * two would be a mismatch nobody could explain. The rupee figure for the
   * screen is `price.amountDue`.
   */
  amountInPaise: number;
  currency: string;
  /** What the window calls us, and the line under it. */
  name: string;
  description: string;
  /** From the billing profile, to save retyping. A field the profile has not
   *  got arrives as `""`. */
  prefill: { name: string; email: string; contact: string };
  /** The gateway carries these back on the webhook, which is how a payment
   *  that arrives without a browser still finds its order. */
  notes: Record<string, string>;
  /** Seconds left before the order stops being payable; `0` once past it. */
  timeoutSeconds: number;
}

/**
 * A purchase in progress, or one that is over: the frozen price, where the
 * payment got to, and — once it has settled — the days it actually bought.
 *
 * Nothing is granted by creating one. No term, no invoice, no access. Those
 * come from settlement, which is `verifyPrepaidOrder`, or the gateway's webhook,
 * or the backend's own sweep. A browser closed between the two has lost
 * nothing, which is the whole reason this record is readable by id.
 */
export interface PrepaidOrder {
  /** The backend's `public_id`. What the status read and the verify are keyed
   *  by, and what the screen holds on to across a reload. */
  id: string;
  status: PrepaidOrderStatus;
  purchaseKind: PurchaseKind;
  planCode: string;
  /** The term as it was quoted. Once `PAID`, `term` below is the one that was
   *  actually written, dated from the capture — read that, not these. */
  startsOn: string;
  endsOn: string;
  /** When this stops being payable — a full ISO instant, half an hour out by
   *  default. */
  expiresAt: string;
  /** The coupon frozen into the order, or `null`. */
  coupon: string | null;
  price: PrepaidPrice;
  /** The days bought, once there are any. `null` until the order is `PAID`. */
  term: { startsOn: string; endsOn: string } | null;
  /**
   * The gateway handoff, or `null`.
   *
   * Non-null only while the order is `CREATED` with an order open against it.
   * A `null` here on anything else is the signal that there is nothing left to
   * reopen and the purchase has to be started again.
   */
  checkout: PrepaidCheckout | null;
}

/**
 * What came of handing the gateway's callback to the backend.
 *
 * Two successes rather than one, and the difference matters to the screen:
 * the money is either settled, in which case there is a term to show, or it is
 * authorised and not yet captured, in which case there is nothing to show yet
 * and the order has to be polled. Neither is a failure.
 *
 * `settled` is read off the order's own status rather than off the HTTP code,
 * because the two say the same thing and only one of them survives the client.
 */
export interface PrepaidSettlement {
  /** `true` once the order is `PAID` and the term is written. */
  settled: boolean;
  order: PrepaidOrder;
}

/**
 * A plan the practice cannot buy right now, and why — what the price list hands
 * the cards so they can be hidden rather than offered and then refused.
 *
 * The backend has no endpoint for this: the flag that gates a credited purchase
 * is not reported anywhere, and asking the quote endpoint means one call per
 * plan, which is refused outright while a term is queued. So the two kinds that
 * cannot be sold today are worked out from the term that is running and the
 * lengths on sale, by the same rule the server classifies with. See
 * `purchaseKindFor`.
 */
export interface PrepaidPlanAvailability {
  /** Whether a card for this plan should be offered at all. */
  sellable: boolean;
  /** What buying it would be, by the rule above. Still the server's to confirm
   *  when the plan is quoted — this decides what is *shown*, never a price. */
  kind: PurchaseKind;
}

/* ── Wire shapes ───────────────────────────────────────────────────────────
   snake_case exactly as the backend sends it. Only `prepaidService` should
   touch these; it maps them into the shapes above so the rest of the app never
   sees the API's casing — the same rule `billing.types` and
   `subscriptions.types` keep.

   Money crosses as a decimal string — `"8016.39"` — and is a number on this
   side. `checkout.amount` is the exception twice over: an integer, and paise.  */

/** One row of `GET /billing/prepaid/plans/`: a term on sale. */
export interface PrepaidPlanResponse {
  /** `"PRO_12M"`. What goes up as `plan_code`. */
  code: string;
  name: string;
  /** What ranks the plans against each other, and so what makes a purchase an
   *  upgrade rather than a different product. Every plan sold today is `1`, so
   *  today this is purely about length. */
  tier_rank: number;
  /** 1, 3, 6 or 12. What replaced the recurring list's `interval` word, and the
   *  only thing on the row that says how long a term runs. */
  duration_months: number;
  currency: string;
  /** The term's price before tax. */
  amount: string;
  /** What the term was listed at before its discount, pre-tax — ₹2,400 against
   *  the ₹1,800 a quarter sells for. The figure the duration row strikes
   *  through. May be absent, or equal to `amount` on a term with no discount
   *  on it, in which case nothing is struck. */
  compare_at_amount?: string;
  /** `amount` with 18% on it — the taxed list price, and **not what a purchase
   *  will cost**: it knows nothing of a coupon or of credit. Always quote.
   *  Not the struck figure: that is `compare_at_amount` above. */
  gross_amount: string;
  /** `amount` divided down to a month, pre-tax. For comparing the four lengths
   *  on the cards, and for nothing that gets charged. */
  monthly_equivalent: string;
}

/** A term as both `GET /billing/prepaid/terms/` and a quote's `current_term`
 *  carry it. The quote's copy has no `status` on it, which is the only
 *  difference between the two. */
export interface PrepaidTermResponse {
  plan_code: string;
  /** `"PREPAID"` | `"ONE_TIME"`. */
  billing_type: string;
  /** Absent on the copy inside a quote. */
  status?: string | null;
  period_start: string;
  period_end: string;
}

/** What `GET /billing/prepaid/terms/` answers. Both keys can be `null`, and
 *  that is an ordinary answer rather than an empty one to apologise for. */
export interface PrepaidTermsResponse {
  current?: PrepaidTermResponse | null;
  queued?: PrepaidTermResponse | null;
}

/** The credit block on a quote or an order — present only where the purchase
 *  replaces a term that still had days left. */
export interface TermCreditResponse {
  amount: string;
  basis_amount: string;
  unused_days: number;
  term_days: number;
}

/**
 * The body `POST /billing/prepaid/quote/` takes.
 *
 * The plan by code, and a coupon where there is one. No figures: what this
 * costs is the server's to say, which is the entire point of asking. The
 * workspace is the bearer token's, as everywhere.
 *
 * `coupon_code` goes up only where there is one — a blank string is read as no
 * coupon by the backend either way, and leaving the key off keeps the request
 * the same shape the purchase will be made with.
 */
export interface PrepaidQuoteRequest {
  plan_code: string;
  coupon_code?: string;
}

/**
 * What a quote answers with: the whole summary, and the server's reading of
 * what is being bought.
 *
 * `tax_rate_percent` comes back `"18"` here and `"18.00"` on an order. The same
 * rate, written by two different parts of the backend — the setting and the
 * stored column — which is why it is carried as a label and never compared.
 */
export interface PrepaidQuoteResponse {
  /** `"NEW"` | `"RENEWAL"` | `"UPGRADE"` | `"DOWNGRADE"` |
   *  `"INTERNAL_CONVERSION"`. */
  purchase_kind: string;
  plan_code: string;
  starts_on: string;
  ends_on: string;
  current_term?: PrepaidTermResponse | null;
  credit?: TermCreditResponse | null;
  currency: string;
  base_amount: string;
  /** `""` rather than null or absent where no coupon applied. */
  coupon_code?: string | null;
  discount_amount: string;
  credit_amount: string;
  taxable_amount: string;
  cgst_amount: string;
  sgst_amount: string;
  igst_amount: string;
  tax_rate_percent: string;
  total_amount: string;
  /** What gets charged. Equal to `total_amount`, and what goes back up as
   *  `expected_total`. */
  amount_due: string;
}

/**
 * The body `POST /billing/prepaid/orders/` takes.
 *
 * The same purchase the quote was asked about, plus the figure it answered
 * with. `expected_total` is a **staleness check and never a price**: the
 * backend prices the order itself and refuses the request outright if what the
 * reader was shown is no longer what it costs, which is the one thing standing
 * between a credit that dropped at midnight and an invoice nobody agreed to.
 */
export interface PrepaidOrderRequest {
  plan_code: string;
  coupon_code?: string;
  /** The quote's `amount_due`, as the decimal string it arrived as — passed
   *  through rather than re-printed from a number, so the comparison is made
   *  against exactly what was displayed. */
  expected_total: string;
}

/** The gateway handoff on a `CREATED` order. */
export interface PrepaidCheckoutResponse {
  /** The *public* key id. */
  key_id: string;
  /** Razorpay's order id, `order_…`. */
  order_id: string;
  /** **Integer paise.** The one figure on any of these responses that is not a
   *  decimal string of rupees. */
  amount: number;
  currency: string;
  name: string;
  description: string;
  prefill?: { name?: string; email?: string; contact?: string } | null;
  notes?: Record<string, string> | null;
  /** Seconds until the order expires; `0` once it has. */
  timeout: number;
}

/**
 * The order as all three of create, read and verify answer it — one shape, so
 * one mapping.
 *
 * It carries the quote frozen at the moment it was accepted, which is what
 * makes it an order rather than a price: those figures do not move again
 * whatever the day does to a credit.
 */
export interface PrepaidOrderResponse {
  public_id: string;
  /** `"CREATED"` | `"PAID"` | `"SUPERSEDED"` | `"EXPIRED"` | `"FAILED"`. */
  status: string;
  purchase_kind: string;
  plan_code: string;
  starts_on: string;
  ends_on: string;
  /** A full ISO instant, unlike the dates above. */
  expires_at: string;
  currency: string;
  base_amount: string;
  coupon_code?: string | null;
  discount_amount: string;
  credit_amount: string;
  taxable_amount: string;
  cgst_amount: string;
  sgst_amount: string;
  igst_amount: string;
  /** `"18.00"` here, where a quote says `"18"`. */
  tax_rate_percent: string;
  total_amount: string;
  amount_due: string;
  /** The days actually written, once the order is `PAID` — dated from the
   *  capture rather than from when it was quoted. */
  term?: { period_start: string; period_end: string } | null;
  /** Non-null only while `CREATED` with a gateway order open. */
  checkout?: PrepaidCheckoutResponse | null;
}

/**
 * The body `POST …/orders/{id}/verify/` takes: the three fields Razorpay's
 * success handler hands over, verbatim.
 *
 * Verbatim is the rule. The signature is over the order and payment ids
 * together, and the amount and status that decide the outcome are read from the
 * gateway by the backend rather than from this body — so there is nothing here
 * worth editing and nothing here that is trusted.
 */
export interface VerifyPrepaidOrderRequest {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

/**
 * How a plan's length maps onto the slug the duration rows, the checkout URL
 * and the renewal arithmetic are all keyed by.
 *
 * The same four terms the recurring price list already drew, reached by a
 * different road: that one named them with an `interval` word and this one
 * sends `duration_months`, so the translation is by number here. Nothing new is
 * on sale — these are the lengths this app has a row for, and a length not in
 * here is dropped rather than rounded to the nearest one, for exactly the
 * reason `CYCLES` in `plansService` gives.
 */
export const PREPAID_CYCLES: Record<number, BillingCycleId> = {
  1: "monthly",
  3: "quarterly",
  6: "half-yearly",
  12: "yearly",
};
